import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { systemSettingsApi } from '../api/system-settings';
import { useAuth } from './AuthContext';

export interface GlobalConfig {
  currency: {
    default: string;
    symbol: string;
  };
  system: {
    timezone: string;
    dateFormat: string;
    language: string;
  };
  facility: {
    name: string;
    address: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    phone: string;
    email: string;
    website: string;
    licenseNumber: string;
  };
}

export interface GlobalConfigContextType {
  config: GlobalConfig | null;
  loading: boolean;
  error: string | null;
  refreshConfig: () => Promise<void>;
  formatCurrency: (amount: number) => string;
  formatDate: (date: Date) => string;
  formatDateTime: (date: Date) => string;
}

const defaultConfig: GlobalConfig = {
  currency: {
    default: 'ZAR',
    symbol: 'R',
  },
  system: {
    timezone: 'Africa/Johannesburg',
    dateFormat: 'DD/MM/YYYY',
    language: 'en',
  },
  facility: {
    name: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    phone: '',
    email: '',
    website: '',
    licenseNumber: '',
  },
};

export const GlobalConfigContext = createContext<GlobalConfigContextType | undefined>(undefined);

export const useGlobalConfig = (): GlobalConfigContextType => {
  const context = useContext(GlobalConfigContext);
  if (context === undefined) {
    throw new Error('useGlobalConfig must be used within a GlobalConfigProvider');
  }
  return context;
};

interface GlobalConfigProviderProps {
  children: ReactNode;
}

export const GlobalConfigProvider: React.FC<GlobalConfigProviderProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [config, setConfig] = useState<GlobalConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      const [facilityResponse, currencyResponse, systemResponse] = await Promise.all([
        systemSettingsApi.getFacilityInfo(),
        systemSettingsApi.getCurrencyInfo(),
        systemSettingsApi.getSystemInfo(),
      ]);

      const newConfig: GlobalConfig = {
        facility: {
          name: facilityResponse.data.name || '',
          address: facilityResponse.data.address || '',
          city: facilityResponse.data.city || '',
          state: facilityResponse.data.state || '',
          postalCode: facilityResponse.data.postalCode || '',
          country: facilityResponse.data.country || '',
          phone: facilityResponse.data.phone || '',
          email: facilityResponse.data.email || '',
          website: facilityResponse.data.website || '',
          licenseNumber: (facilityResponse.data as any).licenseNumber || '',
        },
        currency: {
          default: currencyResponse.data.default || 'ZAR',
          symbol: currencyResponse.data.symbol || 'R',
        },
        system: {
          timezone: systemResponse.data.timezone || 'Africa/Johannesburg',
          dateFormat: systemResponse.data.dateFormat || 'DD/MM/YYYY',
          language: systemResponse.data.language || 'en',
        },
      };

      setConfig(newConfig);
    } catch (err) {
      console.error('Failed to load global configuration:', err);
      setError('Failed to load system configuration');
      // Use default config as fallback
      setConfig(defaultConfig);
    } finally {
      setLoading(false);
    }
  };

  const refreshConfig = async () => {
    await loadConfig();
  };

  const formatCurrency = (amount: number): string => {
    const currentConfig = config || defaultConfig;
    return `${currentConfig.currency.symbol}${amount.toFixed(2)}`;
  };

  const formatDate = (date: Date): string => {
    const currentConfig = config || defaultConfig;
    const timezone = currentConfig.system.timezone;
    
    const options: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };

    return new Intl.DateTimeFormat('en-GB', options).format(date);
  };

  const formatDateTime = (date: Date): string => {
    const currentConfig = config || defaultConfig;
    const timezone = currentConfig.system.timezone;
    
    const options: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };

    return new Intl.DateTimeFormat('en-GB', options).format(date);
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setConfig(defaultConfig);
      setLoading(false);
      setError(null);
      return;
    }
    loadConfig();
  }, [isAuthenticated, authLoading]);

  const value: GlobalConfigContextType = {
    config,
    loading,
    error,
    refreshConfig,
    formatCurrency,
    formatDate,
    formatDateTime,
  };

  return (
    <GlobalConfigContext.Provider value={value}>
      {children}
    </GlobalConfigContext.Provider>
  );
}; 